import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  PoListViewAction,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoTagType,
} from "@po-ui/ng-components";
import { Project } from "./interfaces/project";
import { Published } from "./enums/published";
import { DependencyTrackProjectsService } from "./services/dependency-track-projects/dependency-track-projects.service";
import { DependencyTrackVulnerabilitiesComponent } from "./components/dependency-track-vulnerabilities/dependency-track-vulnerabilities.component";
import { DependencyTrackProjectComponent } from "./components/dependency-track-project/dependency-track-project.component";
import { Status } from "./enums/status";
import { MainService } from "../../app.service";
import {
  BehaviorSubject,
  switchMap,
} from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ProjectOperations } from "./enums/project-operations";

@Component({
  selector: "app-dependency-track",
  templateUrl: "./dependency-track.component.html",
  styleUrls: ["./dependency-track.component.css"],
})
export class DependencyTrackComponent implements OnInit, OnDestroy {
  @ViewChild(DependencyTrackVulnerabilitiesComponent, { static: true })
  dependencyTrackDetails: DependencyTrackVulnerabilitiesComponent;

  @ViewChild(DependencyTrackProjectComponent, { static: true })
  dependencyTrackProjectComponent: DependencyTrackProjectComponent;

  @ViewChild("modalDeleteProject", { static: true }) modalDeleteProject: PoModalComponent

  listActions: Array<PoListViewAction> = [];
  projects: Array<Project> = [];
  hasNext: boolean = false;
  loading: boolean = false;
  projectAnalyzed: string | undefined;
  analyzing: boolean = false;
  projectName: string = "";
  projectNameToDelete: string = "";
  placeHolderProjectName: string = "";
  modalPrimaryAction: PoModalAction;
  modalSecondaryAction: PoModalAction;

  // Filtros
  filterTeam: Array<string> = [];
  filterProductOwner: string = "";
  filterStatus: string = "";
  filterVulnerability: string = "";
  filterSeverity: string = "";
  filterComponentName: string = "";
  filterComponentVersion: string = "";
  showFilters: boolean = false;
  showComponents: boolean = false;
  isComponentSearch: boolean = false;

  teamFilterOptions: Array<any> = [];

  statusFilterOptions: Array<any> = [
    { label: "Todos os status", value: "all" },
    { label: "Ativo", value: "1" },
    { label: "Inativo", value: "0" }
  ];

  severityFilterOptions: Array<any> = [
    { label: "Todos os níveis", value: "all" },
    { label: "Crítica", value: "CRITICAL" },
    { label: "Alta", value: "HIGH" },
    { label: "Média", value: "MEDIUM" },
    { label: "Baixa", value: "LOW" },
  ];

  private limit = 10;
  private page$ = new BehaviorSubject(1);
  private isNewSearch = false;

  constructor(
    private destroyRef: DestroyRef,
    private poNotificationService: PoNotificationService,
    private dependencyTrackProjectsService: DependencyTrackProjectsService,
    private mainService: MainService,
  ) { }

  ngOnInit(): void {
    this.setListActions();
    this.loadTeamOptions();
    this.isNewSearch = true;
    this.getProjects();
  }

  ngOnDestroy(): void {
    this.page$.complete();
  }

  getStatusLabel(status: string): string {
    return (
      {
        [Status.Active]: "Ativo",
        [Status.Inactive]: "Inativo",
      }[status] || "Desconhecido"
    );
  }

  getStatusType(status: string): PoTagType {
    return (
      {
        [Status.Active]: PoTagType.Success,
        [Status.Inactive]: PoTagType.Danger,
      }[status] || PoTagType.Neutral
    );
  }

  isPublished(published: Published): string {
    return this.getPublishedLabel(published);
  }

  getPublishedLabel(published: Published): string {
    return (
      {
        [Published.Yes]: "Sim",
        [Published.No]: "Não",
      }[published] || ""
    );
  }

  showMore(): void {
    if (!this.loading && this.hasNext) {
      this.page$.next(this.page$.value + 1);
    }
  }

  onSaveProject(project: Project): void {
    const projectToChange = this.projects.findIndex((p) => p.id === project.id);

    if (projectToChange > -1) {
      const projects = this.projects;

      projects[projectToChange] = project;

      this.projects = [...projects];
      return;
    }

    this.projects = [...this.projects, project];
  }

  refreshProjects(): void {
    this.isNewSearch = true;
    this.page$.next(1);
  }

  addProject(): void {
    this.dependencyTrackProjectComponent.open(ProjectOperations.Add);
  }

  editProject(project: Project): void {
    this.dependencyTrackProjectComponent.open(ProjectOperations.Edit, project);
  }

  onProjectNameToDeleteChange(): void {
    this.modalPrimaryAction = {
      ...this.modalPrimaryAction,
      disabled: this.projectNameToDelete !== this.placeHolderProjectName
    };
  }

  private setListActions(): void {
    this.listActions = [
      {
        label: "Vulnerabilidades",
        action: this.details.bind(this),
        icon: "po-icon-search",
        separator: true,
        disabled: this.disableActions.bind(this),
      },
      {
        label: "Resync",
        icon: "po-icon-exam",
        action: this.executeAnalysis.bind(this),
        separator: true,
        disabled: this.disableAnalysisAction.bind(this),
      },
      {
        label: "Editar",
        icon: "po-icon-edit",
        action: this.editProject.bind(this),
        separator: true,
        disabled: this.disableEditAction.bind(this),
      },
    ];

    if ((sessionStorage.getItem("Role") || "") === "superuser") {
      this.listActions = [...this.listActions, {
        label: "Excluir",
        icon: "po-icon-delete",
        action: this.confirmDeleteProject.bind(this),
        separator: true,
        type: "danger",
      }];
    }
  }

  private getProjects(): void {
    this.page$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap((page) => {
          const createdBy = this.filterProductOwner || "";
          const productOwner = this.filterProductOwner || "";
          const teams = this.filterTeam.length > 0 ? this.filterTeam : [];
          const status = this.filterStatus === "all" ? "" : this.filterStatus;
          const vulnerability = this.filterVulnerability || "";
          const severity = this.filterSeverity === "all" ? "" : this.filterSeverity;
          const componentName = this.filterComponentName || "";
          const componentVersion = this.filterComponentVersion || "";

          this.loading = true;
          return this.dependencyTrackProjectsService.getProjects(
            createdBy,
            productOwner,
            teams,
            this.projectName,
            status,
            page,
            this.limit,
            vulnerability,
            severity,
            componentName,
            componentVersion
          );
        })
      )
      .subscribe({
        next: ({ items, has_next }) => {
          if (this.page$.value === 1 || this.isNewSearch) {
            this.projects = [...items];
            this.isNewSearch = false;
          } else {
            const projects = [...this.projects, ...items];
            this.projects = [];
            this.projects = Array.from(
              new Map(projects.map((u) => [u.id, u])).values()
            );
          }
          this.hasNext = has_next;
          this.loading = false;
        },
        complete: () => {
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  search(): void {
    this.isComponentSearch = this.showComponents;
    this.isNewSearch = true;

    this.page$.next(1);
  }

  private details(project: Project): void {
    if (project.last_scan) {
      this.dependencyTrackDetails.open(project);
      return;
    }

    this.poNotificationService.warning(
      `Ainda não existem análises disponíveis para o projeto ${project.name}.`
    );
  }

  private executeAnalysis(project: Project): void {
    if (!project.id) {
      this.poNotificationService.error('ID do projeto não encontrado.');
      return;
    }

    this.analyzing = true;
    this.projectAnalyzed = project.id;

    this.dependencyTrackProjectsService.postProjectsScan(project.id, project).subscribe({
      next: (project) => {
        this.onSaveProject(project);

        if (project.last_scan) {
          this.poNotificationService.success(
            `Análise concluída para o projeto ${project.name}!`
          );
          return;
        }

        this.poNotificationService.warning(
          `O projeto ${project.name} ainda não possui análises disponíveis pelo Dependency Track.`
        );
      },
      complete: () => {
        this.analyzing = false;
      },
      error: () => {
        this.analyzing = false;

        this.poNotificationService.warning(
          `Falha na análise do projeto ${project.name}. Tente novamente.`
        );
      },
    });
  }

  private disableActions(project: Project): boolean {
    return this.analyzing && this.projectAnalyzed === project.id;
  }

  private disableEditAction(project: Project): boolean {
    // Desabilita se estiver analisando OU se não pode editar
    return this.disableActions(project) || !this.canAccessProject(project);
  }

  private disableAnalysisAction(project: Project): boolean {
    // Desabilita se estiver analisando OU se não pode executar análise
    return this.disableActions(project) || !this.canAccessProject(project);
  }


  private canAccessProject(project: Project): boolean {
    const currentUser = sessionStorage.getItem("User") || "";
    const currentUserTeam = sessionStorage.getItem("Area") || "";
    const currentUserRole = sessionStorage.getItem("Role") || "";

    const isSuperuser = currentUserRole === "superuser";
    const isCreator = project.created_by === currentUser;
    const isProductOwner = project.product_owner === currentUser;
    const isSameTeam = project.team === currentUserTeam;

    return isSuperuser || isCreator || isProductOwner || isSameTeam;
  }

  private confirmDeleteProject(project: Project): void {
    this.modalDeleteProject.title = `Excluir projeto ${project.name}`;
    this.placeHolderProjectName = project.name || "";
    this.projectNameToDelete = "";
    this.modalPrimaryAction = {
      action: () => this.deleteProject(project.id || "", this.projectNameToDelete),
      label: 'Excluir',
      disabled: this.projectNameToDelete !== project.name,
      danger: true
    };
    this.modalSecondaryAction = {
      action: () => this.modalDeleteProject.close(),
      label: 'Cancelar'
    };

    this.modalDeleteProject.open()
  }

  private deleteProject(projectId: string, projectName: string): void {
    this.dependencyTrackProjectsService.deleteProjects(projectId, projectName).subscribe({
      next: () => {
        this.projects = this.projects.filter(p => p.id !== projectId);
      },
      complete: () => {
        this.projectNameToDelete = "";
        this.poNotificationService.success('Projeto excluído com sucesso.');
        this.modalDeleteProject.close();
      },
      error: () => {
        this.projectNameToDelete = "";
        this.poNotificationService.error('Falha ao excluir o projeto. Tente novamente.');
        this.modalDeleteProject.close();
      }
    });
  }

  clearFilters(): void {
    this.filterTeam = [];
    this.filterProductOwner = "";
    this.filterStatus = "all";
    this.filterVulnerability = "";
    this.filterSeverity = "all";
    this.filterComponentName = "";
    this.filterComponentVersion = "";
    this.projectName = "";
    this.isComponentSearch = false;
    this.isNewSearch = true;
    this.search();
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filterTeam.length || 
      this.filterProductOwner || 
     this.filterStatus || 
      this.filterVulnerability ||
     this.filterSeverity ||
      this.filterComponentName ||
      this.filterComponentVersion ||
      this.projectName
    );
  }

  toggleFilters(): void {
  this.showFilters = !this.showFilters;

  if (this.showFilters) {
    this.showComponents = false;
    
    this.isComponentSearch = false; 
    
    this.filterComponentName = '';
    this.filterComponentVersion = '';
    
    this.isNewSearch = true;
    this.page$.next(1);
  }
}

toggleComponents(): void {
  this.showComponents = !this.showComponents;

  if (this.showComponents) {
    this.showFilters = false;
    
    this.isComponentSearch = true; 
    
    this.projectName = '';
    this.filterProductOwner = '';
    this.filterTeam = [];
    this.filterStatus = 'all';
    this.filterVulnerability = '';
    this.filterSeverity = 'all';
    
    this.isNewSearch = true;
    this.page$.next(1);
  }
}

  private loadTeamOptions(): void {
    this.mainService.getAreas().subscribe({
      next: (areas) => {
        this.teamFilterOptions = areas.map(({ area }) => ({
          label: area,
          value: area,
        }));
      },
      error: () => {
        this.teamFilterOptions = [];
      }
    });
  }
}
